import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import useForm from 'hooks/useForm';
import useUploadProfilePicture from 'hooks/useUploadProfilePicture';
import {
  updateWorker,
  uploadGovernmentDocumentFiles,
  deleteGovernmentDocumentFile,
  addGovernmentDocumentFile,
} from 'modules/state-mgmt/onboarding';
import { fetchProfileCustomFieldsConfig } from 'modules/state-mgmt/workerData';
import { actionMapForOtherLoaders } from 'constants/actions';
import { GOV_ID_TYPE_OTHER_OPTION_ID } from 'constants/common';
import { createGuid, formatBackendValidationErrors, prettifyErrorResponse } from 'utils';
import { getImageFormat } from 'utils/files';
import { extractNationalNumber } from 'utils/phoneUtils';
import { findStateByCodeAndCountry } from 'utils/addressUtils';
import { IFile } from 'models/general';
import { IAddress } from 'services/GoogleMapsService';
import { extractCustomFieldCodes } from 'utils/customFieldsUtils';
import { IWorkerCustomField } from 'types/profileCustomFields';

import profileInformationSchema, { IProfileInformationSchema } from '../schema';
import { useProfileInformationFields, PROFILE_INFO_FIELD_CONFIG } from './useProfileInformationFields';

const transformTrades = (beTrades: any[], skilledTrades: any[]) => {
  const trades: string[] = [];
  const otherTradesList: Array<{ OtherText: string; isSaved: boolean }> = [];
  beTrades.forEach(trade => {
    const matchedTrade = skilledTrades.find(st => st.id === trade.id) || null;
    if (!matchedTrade) {
      otherTradesList.push({ OtherText: trade.name, isSaved: false });
    } else if (matchedTrade.masterList) {
      trades.push(matchedTrade.id);
    } else {
      otherTradesList.push({ OtherText: matchedTrade.id, isSaved: true });
    }
  });
  return { trades, otherTradesList };
};

const useProfileInformation = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const {
    workerId,
    languageCode,
    profileCustomFieldsConfig,
    governmentDocumentFiles,
    updateWorkerLoading,
    uploadGovDocsLoading,
    profileCustomFieldsConfigLoading,
    onboarding,
    skilledTrades,
    countries,
    states,
  } = useAppSelector(state => ({
    workerId: state.onboarding?.id || state.user?.workerId,
    languageCode: state.app?.languageCode,
    profileCustomFieldsConfig: state.workerData.profileCustomFieldsConfig || [],
    governmentDocumentFiles: state.onboarding.governmentDocumentFiles || [],
    updateWorkerLoading: state.loader.otherLoaders[actionMapForOtherLoaders.updateWorker],
    uploadGovDocsLoading: state.loader.otherLoaders[actionMapForOtherLoaders.uploadGovernmentDocumentFiles],
    profileCustomFieldsConfigLoading:
      state.loader.otherLoaders[actionMapForOtherLoaders.fetchProfileCustomFieldsConfig],
    onboarding: state.onboarding,
    skilledTrades: state.cache.skilledTrades?.[state.app?.languageCode || 'en'] || [],
    countries: state.cache.countries?.[state.app?.languageCode || 'en'] || [],
    states: state.cache.states?.[state.app?.languageCode || 'en'] || [],
  }));

  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [deleteId, setDeleteId] = useState('');

  const isLoading = updateWorkerLoading?.isLoading || uploadGovDocsLoading?.isLoading;
  const isConfigLoading = !profileCustomFieldsConfigLoading || profileCustomFieldsConfigLoading.isLoading === true;

  const profileFieldConstraintsMap = useMemo(() => {
    const map: Record<
      string,
      { isVisible: boolean; isMandatory: boolean; isSkippable: boolean; skippableTill: number }
    > = {};
    (profileCustomFieldsConfig as IWorkerCustomField[]).forEach(field => {
      map[field.code] = {
        isVisible: field.isVisible,
        isMandatory: field.isMandatory,
        isSkippable: field.isSkippable,
        skippableTill: field.skippableTill,
      };
    });
    return map;
  }, [profileCustomFieldsConfig]);

  const { isLoading: isProfilePictureUploading, uploadProfilePicture } = useUploadProfilePicture({
    successCallback: () => setShowCaptureModal(false),
    failureCallback: () => setShowCaptureModal(false),
  });

  const initialValues: IProfileInformationSchema = useMemo(
    () => ({
      // Personal Information
      firstName: onboarding?.firstName || '',
      middleName: onboarding?.middleName || '',
      lastName: onboarding?.lastName || '',
      dateOfBirth: onboarding?.dateOfBirth || null,
      gender: onboarding?.gender ?? null,
      ethnicityId: onboarding?.ethnicity?.id || null,
      primaryLanguageId: onboarding?.primaryLanguage?.id || null,
      isVeteran: onboarding?.isVeteran ?? null,
      allergies: onboarding?.allergies || null,
      socialSecurityNumber: onboarding?.socialSecurityNumber || null,
      pictureUrl: onboarding?.pictureUrl || null,

      // Government ID
      identificationTypeId: onboarding?.identificationType?.id || null,
      identificationGeographicLocationId: onboarding?.identificationGeographicLocation?.id || null,
      identificationNumber: onboarding?.identificationNumber || null,
      otherIdentificationType: onboarding?.otherIdentificationType || null,
      governmentDocumentFiles: governmentDocumentFiles,

      // Contact Information
      email: onboarding?.email || '',
      mobileNumber: onboarding?.mobilePhoneNumber || '',
      phoneNumber: onboarding?.phoneNumber || '',

      // Home Address
      addressLine1: onboarding?.address?.line1 || '',
      addressLine2: onboarding?.address?.line2 || '',
      country: onboarding?.address?.country?.id || '',
      countryName: onboarding?.address?.country?.name || '',
      state: onboarding?.address?.stateCode || '',
      stateName: onboarding?.address?.stateName || '',
      city: onboarding?.address?.city || '',
      zipCode: onboarding?.address?.zipCode || '',
      county: onboarding?.address?.county || '',
      borough: onboarding?.address?.borough || '',
      latitude: onboarding?.address?.latitude,
      longitude: onboarding?.address?.longitude,

      // Emergency Contact
      emergencyContactName: onboarding?.emergencyContactName || '',
      emergencyContactNumber: onboarding?.emergencyContactPhone || '',
      emergencyContactRelationship: onboarding?.emergencyContactRelationship || '',

      // Worker Trades
      isSupervisor: onboarding?.isSupervisor ?? null,
      isAffiliatedToLaborUnion: onboarding?.isAffiliatedToLaborUnion ?? null,
      isSkilled: onboarding?.isSkilled ?? null,
      laborUnionNumber: onboarding?.laborUnionNumber || '',
      ...transformTrades(onboarding?.trades || [], skilledTrades),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const validationSchema = useMemo(
    () => profileInformationSchema(t, profileFieldConstraintsMap),
    [t, profileFieldConstraintsMap],
  );

  const handleFormSubmit = useCallback(
    async (formValues: IProfileInformationSchema) => {
      const getValidPhoneNumber = (phoneValue: string): string | null => {
        const nationalNumber = extractNationalNumber(phoneValue);
        return nationalNumber && nationalNumber.trim().length > 0 ? phoneValue : null;
      };

      try {
        const workerCustomFieldCodesToValidate = extractCustomFieldCodes(PROFILE_INFO_FIELD_CONFIG);

        const selectedCountry = countries.find((c: any) => c.id === formValues.country) || null;
        const selectedState = findStateByCodeAndCountry(states, formValues.state, selectedCountry);

        const apiData = {
          // Personal Info
          firstName: formValues.firstName?.trim(),
          middleName: formValues.middleName?.trim() || null,
          lastName: formValues.lastName?.trim(),
          dateOfBirth: formValues.dateOfBirth
            ? typeof formValues.dateOfBirth === 'string'
              ? formValues.dateOfBirth
              : (formValues.dateOfBirth as any).format?.('YYYY-MM-DD')
            : null,
          ethnicityId: formValues.ethnicityId || null,
          primaryLanguageId: formValues.primaryLanguageId || null,
          isVeteran: formValues.isVeteran,
          allergies: formValues.allergies?.trim() || null,
          socialSecurityNumber: formValues.socialSecurityNumber || null,
          gender: formValues.gender,

          // Government ID
          identificationTypeId: formValues.identificationTypeId || null,
          ...(formValues.identificationTypeId === GOV_ID_TYPE_OTHER_OPTION_ID
            ? { otherIdentificationType: formValues.otherIdentificationType?.trim() }
            : { identificationGeographicLocationId: formValues.identificationGeographicLocationId }),
          identificationNumber: formValues.identificationNumber || null,

          // Contact Info
          email: formValues.email?.trim() || null,
          mobilePhoneNumber: getValidPhoneNumber(formValues.mobileNumber),
          phoneNumber: getValidPhoneNumber(formValues.phoneNumber),

          // Address
          address: {
            line1: formValues.addressLine1?.trim() || null,
            line2: formValues.addressLine2?.trim() || null,
            city: formValues.city?.trim() || null,
            zipCode: formValues.zipCode?.trim() || null,
            county: formValues.county || null,
            borough: formValues.borough || null,
            latitude: formValues.latitude,
            longitude: formValues.longitude,
            countryId: selectedCountry?.id || '',
            country: selectedCountry
              ? { id: selectedCountry.id, name: selectedCountry.name, code: selectedCountry.code }
              : null,
            stateCode: selectedState ? selectedState.code : formValues.state || '',
            stateName: selectedState ? selectedState.name : formValues.stateName || '',
          },

          // Emergency Contact
          emergencyContactName: formValues.emergencyContactName?.trim() || null,
          emergencyContactPhone: getValidPhoneNumber(formValues.emergencyContactNumber),
          emergencyContactRelationship: formValues.emergencyContactRelationship?.trim() || null,

          // Trades
          isSupervisor: formValues.isSupervisor,
          isAffiliatedToLaborUnion: formValues.isAffiliatedToLaborUnion,
          isSkilled: formValues.isSkilled,
          laborUnionNumber: formValues.laborUnionNumber?.trim() || null,
          trades: formValues.trades,
          otherTradesList: formValues.otherTradesList,
        };

        const res = await dispatch(
          updateWorker({ workerId, data: apiData, languageCode, workerCustomFieldCodesToValidate }),
        );

        if (res.type.includes('fulfilled')) {
          enqueueSnackbar(t('myProfile.successMessages.profileUpdated'), { variant: 'success' });
        } else {
          const errorMessage = formatBackendValidationErrors(res.payload);
          enqueueSnackbar(errorMessage, { variant: 'error', style: { whiteSpace: 'pre-line' } });
        }
      } catch (error) {
        enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
      }
    },
    [dispatch, workerId, languageCode, t, countries, states],
  );

  const onSubmit = useCallback(
    async (formValues: IProfileInformationSchema) => {
      const hasPendingTempFiles = governmentDocumentFiles.filter((d: IFile) => d.tempId).length > 0;
      if (hasPendingTempFiles) {
        dispatch(uploadGovernmentDocumentFiles())
          .then(res => {
            if (res.type.includes('fulfilled')) {
              handleFormSubmit(formValues);
            } else {
              enqueueSnackbar(prettifyErrorResponse(res?.payload), { variant: 'error' });
            }
          })
          .catch(err => enqueueSnackbar(prettifyErrorResponse(err), { variant: 'error' }));
      } else {
        await handleFormSubmit(formValues);
      }
    },
    [dispatch, governmentDocumentFiles, handleFormSubmit],
  );

  const { values, errors, setValue, handleSubmit } = useForm({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const fields = useProfileInformationFields(profileFieldConstraintsMap, values);

  useEffect(() => {
    if (!workerId) return;
    dispatch(fetchProfileCustomFieldsConfig({ workerId, languageCode, showAcceptedProjectsSettingsOnly: true }));
  }, [dispatch, workerId, languageCode]);

  // Sync govt doc files from Redux into form
  useEffect(() => {
    setValue('governmentDocumentFiles', governmentDocumentFiles);
  }, [governmentDocumentFiles, setValue]);

  // Sync trades from Redux into form once skilledTrades reference data has loaded
  useEffect(() => {
    if (!skilledTrades?.length) return;
    const { trades, otherTradesList } = transformTrades(onboarding?.trades || [], skilledTrades);
    setValue('trades', trades);
    setValue('otherTradesList', otherTradesList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skilledTrades]);

  // Sync profile picture from Redux into form
  useEffect(() => {
    setValue('pictureUrl', onboarding.pictureUrl || null);
  }, [onboarding.pictureUrl, setValue]);

  const onUploadProfilePicture = useCallback(
    (imageSrc: string | string[]) => {
      const src = Array.isArray(imageSrc) ? imageSrc[0] : imageSrc;
      uploadProfilePicture({ imageSrc: src });
    },
    [uploadProfilePicture],
  );

  const onAddGovernmentDocumentFile = useCallback(
    (imageSrc: string | string[]) => {
      const sources = Array.isArray(imageSrc) ? imageSrc : [imageSrc];
      sources.forEach(src => {
        const tempId = createGuid();
        const imageFormat = getImageFormat(src);
        dispatch(addGovernmentDocumentFile({ tempId, image: src, fileName: `${tempId}.${imageFormat}` }));
      });
    },
    [dispatch],
  );

  const onDeleteGovernmentDocumentFile = useCallback(
    (file: IFile) => {
      setDeleteId(file.id || file.tempId);
      dispatch(
        deleteGovernmentDocumentFile({
          fileId: file.id,
          tempId: file.tempId,
          workerId,
          languageCode,
        }),
      )
        .then(res => {
          if (!res.type.includes('fulfilled')) {
            enqueueSnackbar(prettifyErrorResponse(res?.payload), { variant: 'error' });
          }
        })
        .catch(err => enqueueSnackbar(prettifyErrorResponse(err), { variant: 'error' }))
        .finally(() => setDeleteId(''));
    },
    [dispatch, workerId, languageCode],
  );

  const onToggleTrade = useCallback(
    (tradeId: string) => {
      const current: string[] = values.trades || [];
      const next = current.includes(tradeId) ? current.filter(id => id !== tradeId) : [...current, tradeId];
      setValue('trades', next);
    },
    [values.trades, setValue],
  );

  const onAddOtherTrade = useCallback(() => {
    setValue('otherTradesList', [...(values.otherTradesList || []), { isSaved: false, OtherText: '' }]);
  }, [values.otherTradesList, setValue]);

  const onChangeOtherTrade = useCallback(
    (index: number, text: string) => {
      const updated = (values.otherTradesList || []).map((item, i) =>
        i === index ? { ...item, OtherText: text } : item,
      );
      setValue('otherTradesList', updated);
    },
    [values.otherTradesList, setValue],
  );

  const onRemoveOtherTrade = useCallback(
    (index: number) => {
      setValue(
        'otherTradesList',
        (values.otherTradesList || []).filter((_, i) => i !== index),
      );
    },
    [values.otherTradesList, setValue],
  );

  const onAddressSelect = useCallback(
    (address: IAddress) => {
      const selectedCountry = countries.find((c: any) => c.code === address.countryCode) || null;
      const selectedState = selectedCountry
        ? states.find((s: any) => {
            if (s.code !== address.stateCode) return false;
            return s.country === selectedCountry.name || s.country === selectedCountry.id || s.country === selectedCountry.code;
          })
        : null;

      setValue('addressLine1', address.addressLine1 || '');
      setValue('addressLine2', address.addressLine2 || '');
      setValue('city', address.city || '');
      setValue('zipCode', address.postalCode || '');
      setValue('country', selectedCountry?.id || '');
      setValue('state', selectedState?.code || '');
      setValue('countryName', selectedCountry?.name || '');
      setValue('stateName', selectedState?.name || '');
      if (address.county) setValue('county', address.county);
      if (address.borough) setValue('borough', address.borough);
      if (address.latitude !== undefined) setValue('latitude', address.latitude as any);
      if (address.longitude !== undefined) setValue('longitude', address.longitude as any);
    },
    [setValue, countries, states],
  );

  return {
    values,
    errors,
    setValue,
    handleSubmit,
    isLoading,
    isConfigLoading,
    isProfilePictureUploading,
    showCaptureModal,
    setShowCaptureModal,
    deleteId,
    governmentDocumentFiles,
    workerId,
    onUploadProfilePicture,
    onAddGovernmentDocumentFile,
    onDeleteGovernmentDocumentFile,
    onToggleTrade,
    onAddOtherTrade,
    onChangeOtherTrade,
    onRemoveOtherTrade,
    onAddressSelect,
    canEditEmail: onboarding?.canEditEmail || false,
    canEditMobilePhoneNumber: onboarding?.canEditMobilePhoneNumber || false,
    languageCode,
    ...fields,
  };
};

export default useProfileInformation;
